import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [fleetManager, setFleetManager] = useState(null);
  const [swapStationManager, setSwapStationManager] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [dataRecords, setDataRecords] = useState(null);
  const [isReadonly, setIsReadonly] = useState(false);
  // Per-account page permissions. Empty array = full access (legacy accounts).
  const [permissions, setPermissions] = useState([]);
  const [linkedRoles, setLinkedRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const interceptorId = useRef(null);

  const clearAllRoles = () => {
    setFleetManager(null);
    setSwapStationManager(null);
    setAdmin(null);
    setDataRecords(null);
  };

  const persistSession = (token, userType, ro, perms) => {
    localStorage.setItem('token', token);
    localStorage.setItem('userType', userType);
    localStorage.setItem('isReadonly', ro ? 'true' : 'false');
    localStorage.setItem('permissions', JSON.stringify(perms || []));
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setIsReadonly(!!ro);
    setPermissions(perms || []);
  };

  const clearSession = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userType');
    localStorage.removeItem('isReadonly');
    localStorage.removeItem('permissions');
    localStorage.removeItem('adminPermissions'); // legacy key
    delete axios.defaults.headers.common['Authorization'];
    clearAllRoles();
    setIsReadonly(false);
    setPermissions([]);
  };

  useEffect(() => {
    interceptorId.current = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 403 && error.response?.data?.message?.includes('Read-only')) {
          alert('You have read-only access. This action is not permitted.');
          return Promise.reject(error);
        }
        if (error.response?.status === 401 && !error.config?.url?.includes('/api/auth/')) {
          clearSession();
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
    return () => {
      if (interceptorId.current !== null) {
        axios.interceptors.response.eject(interceptorId.current);
      }
    };
  }, []);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const applyProfileExtras = (data) => {
    if (data.permissions !== undefined) {
      setPermissions(data.permissions || []);
      localStorage.setItem('permissions', JSON.stringify(data.permissions || []));
    }
    if (data.isReadonly !== undefined) {
      setIsReadonly(!!data.isReadonly);
      localStorage.setItem('isReadonly', data.isReadonly ? 'true' : 'false');
    }
  };

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const userType = localStorage.getItem('userType');

      if (token && userType) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        setIsReadonly(localStorage.getItem('isReadonly') === 'true');
        try {
          const savedPerms = localStorage.getItem('permissions');
          if (savedPerms) setPermissions(JSON.parse(savedPerms));
        } catch (e) { /* ignore parse errors */ }
        if (userType === 'fleet_manager') {
          const response = await axios.get('/api/auth/profile');
          setFleetManager(response.data.fleetManager);
          applyProfileExtras(response.data);
        } else if (userType === 'swap_station_manager') {
          const response = await axios.get('/api/auth/admin/profile');
          setSwapStationManager(response.data.swapStationManager);
        } else if (userType === 'admin') {
          const response = await axios.get('/api/auth/user-admin/profile');
          setAdmin(response.data.admin);
          applyProfileExtras(response.data);
        } else if (userType === 'data_records') {
          const response = await axios.get('/api/auth/data-records/profile');
          setDataRecords(response.data.dataRecords);
          applyProfileExtras(response.data);
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('userType');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  // Generic handler for login responses that carry { token, permissions, isReadonly, <roleObject> }
  const applyLoginResponse = (data, userType) => {
    persistSession(data.token, userType, data.isReadonly, data.permissions);
    clearAllRoles();
    if (userType === 'fleet_manager') setFleetManager(data.fleetManager);
    if (userType === 'swap_station_manager') setSwapStationManager(data.swapStationManager);
    if (userType === 'admin') setAdmin(data.admin);
    if (userType === 'data_records') setDataRecords(data.dataRecords);
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post('/api/auth/login', { email, password });
      applyLoginResponse(response.data, 'fleet_manager');
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Login failed'
      };
    }
  };

  const unifiedLogin = async (email, password) => {
    try {
      const response = await axios.post('/api/auth/unified-login', { email, password });
      const data = response.data;
      const { userType } = data;

      if (userType === 'swap_station_manager' && data.requiresStationSelection) {
        return {
          success: true,
          requiresStationSelection: true,
          pendingToken: data.pendingToken,
          stations: data.stations || [],
          defaultStationId: data.defaultStationId || null,
          userType
        };
      }

      applyLoginResponse(data, userType);
      return { success: true, userType };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Invalid credentials'
      };
    }
  };

  // Step 1 — validate credentials, receive a short-lived pending token + list of stations.
  const swapStationManagerLogin = async (email, password) => {
    try {
      const response = await axios.post('/api/auth/admin/login', { email, password });
      const { pendingToken, isReadonly: ro, stations, defaultStationId } = response.data;
      return {
        success: true,
        pendingToken,
        isReadonly: !!ro,
        stations: stations || [],
        defaultStationId: defaultStationId || null
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Login failed'
      };
    }
  };

  // Step 2 — commit a station; receive the full JWT and log this login into history.
  const swapStationManagerSelectStation = async (pendingToken, swapStationId) => {
    try {
      const response = await axios.post('/api/auth/admin/select-station', {
        pendingToken,
        swap_station_id: swapStationId
      });
      const { token, swapStationManager, isReadonly: ro } = response.data;
      persistSession(token, 'swap_station_manager', ro, []);
      clearAllRoles();
      setSwapStationManager(swapStationManager);
      setFleetManager(null);
      setAdmin(null);
      setDataRecords(null);
      setIsReadonly(!!ro);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Station selection failed'
      };
    }
  };

  const adminLogin = async (email, password) => {
    try {
      const response = await axios.post('/api/auth/user-admin/login', { email, password });
      const { token, admin, isReadonly: ro } = response.data;

      localStorage.setItem('token', token);
      localStorage.setItem('userType', 'admin');
      localStorage.setItem('isReadonly', ro ? 'true' : 'false');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setAdmin(admin);
      setFleetManager(null);
      setSwapStationManager(null);
      setDataRecords(null);
      setIsReadonly(!!ro);

      applyLoginResponse(response.data, 'admin');
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Login failed'
      };
    }
  };

  const dataRecordsLogin = async (email, password) => {
    try {
      const response = await axios.post('/api/auth/data-records/login', { email, password });
      applyLoginResponse(response.data, 'data_records');
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Login failed'
      };
    }
  };

  const requestOtp = async (email, userType) => {
    try {
      await axios.post('/api/auth/request-otp', { email, userType });
      return { success: true };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to send OTP' };
    }
  };

  const verifyOtpLogin = async (email, otp, userType) => {
    try {
      const response = await axios.post('/api/auth/verify-otp', { email, otp, userType });

      // Swap station manager OTP login still needs a station picker step.
      if (response.data.requiresStationSelection) {
        return {
          success: true,
          requiresStationSelection: true,
          pendingToken: response.data.pendingToken,
          stations: response.data.stations || [],
          defaultStationId: response.data.defaultStationId || null
        };
      }

      applyLoginResponse(response.data, userType);
      return { success: true };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'OTP verification failed' };
    }
  };

  const forgotPassword = async (email, userType) => {
    try {
      await axios.post('/api/auth/forgot-password', { email, userType });
      return { success: true };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to send reset OTP' };
    }
  };

  const resetPassword = async (email, otp, newPassword, userType) => {
    try {
      await axios.post('/api/auth/reset-password', { email, otp, newPassword, userType });
      return { success: true };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Password reset failed' };
    }
  };

  const register = async (userData) => {
    try {
      await axios.post('/api/auth/register', userData);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Registration failed'
      };
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchLinkedAccounts();
    }
  }, [fleetManager, swapStationManager, admin, dataRecords]);

  const fetchLinkedAccounts = async () => {
    try {
      const res = await axios.get('/api/auth/linked-accounts');
      setLinkedRoles(res.data.roles || []);
    } catch (e) {
      setLinkedRoles([]);
    }
  };

  const switchAccount = async (role, accountId) => {
    try {
      const res = await axios.post('/api/auth/switch-account', { role, account_id: accountId });
      const data = res.data;
      const userType = data.userType || role;

      persistSession(data.token || res.data.token, userType, data.isReadonly, data.permissions);
      clearAllRoles();

      if (data.fleetManager) setFleetManager(data.fleetManager);
      if (data.swapStationManager) setSwapStationManager(data.swapStationManager);
      if (data.admin) setAdmin(data.admin);
      if (data.dataRecords) setDataRecords(data.dataRecords);
      if (data.customerAdmin) {
        // customer_admin shares the same session shape — store as admin for nav purposes
      }

      return { success: true, userType };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Switch failed' };
    }
  };

  const logout = () => {
    clearSession();
    setLinkedRoles([]);
  };

  const canManageFleet = !!(fleetManager && fleetManager.can_manage_fleet);
  const isCustomer = canManageFleet;
  const isSuperAdmin = !!(admin && admin.is_super);

  const hasPermission = (permissionKey) => {
    if (!permissions || permissions.length === 0) return true;
    return permissions.includes(permissionKey);
  };

  const isPermissionReadonly = (permissionKey) => {
    if (!permissions || permissions.length === 0) return isReadonly;
    if (permissions.includes(permissionKey + ':readonly')) return true;
    return isReadonly;
  };

  const value = {
    fleetManager,
    swapStationManager,
    admin,
    dataRecords,
    isReadonly,
    permissions,
    adminPermissions: permissions, // legacy alias
    hasPermission,
    isPermissionReadonly,
    canManageFleet,
    isCustomer,
    isSuperAdmin,
    linkedRoles,
    loading,
    login,
    unifiedLogin,
    swapStationManagerLogin,
    swapStationManagerSelectStation,
    adminLogin,
    dataRecordsLogin,
    requestOtp,
    verifyOtpLogin,
    forgotPassword,
    resetPassword,
    register,
    switchAccount,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
